/**
 * Block 13 PR-G Phase 3 — fromQuote deeplink seeding hook.
 *
 * Reads `?fromQuote=<uuid>` from URL search params, loads the saved Block 12
 * quote из IDB, maps it через Step 1 `quoteToContractMapping`, exposes seed +
 * loading + error state. Consumer (Phase 4 ContractForm container) merges
 * seed с form defaults at mount.
 *
 * Lifecycle:
 *   - `quoteId === null` (no `?fromQuote` param) → no-op, {seed: null, loading: false, error: null}
 *   - `quoteId` present → useEffect loads quote через `loadSavedQuote`:
 *     - resolved with quote → seed populated via mapQuoteStateToContractDraft
 *     - resolved with undefined (not found) → error = 'quote_not_found'
 *     - rejected (IDB unavailable / connection issue) → error = 'idb_unavailable'
 *
 * StrictMode safety: cancelled-flag pattern (B-121 lesson) — double-invoke
 * effects не triggers race condition; cleanup function flips `cancelled = true`
 * перед next effect run.
 *
 * Reference:
 *   - ADR-0025 §Q7 (canonical mapping + URL pattern)
 *   - apps/web/src/features/handles-catalog/HandlesCatalog.tsx (cancelled-flag reference)
 *   - apps/web/src/features/calculator/quote/state/savedQuotesIdb.ts (loadSavedQuote API)
 */

import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import type { ContractDraft } from '@pomoshnik/shared';

import { loadSavedQuote } from '@/features/calculator/quote/state/savedQuotesIdb';

import { mapQuoteStateToContractDraft } from '../services/quoteToContractMapping';

export type FromQuoteSeedingError = 'quote_not_found' | 'idb_unavailable';

export interface FromQuoteSeedingResult {
  seed: Partial<ContractDraft> | null;
  loading: boolean;
  error: FromQuoteSeedingError | null;
}

const NO_SEED: FromQuoteSeedingResult = {
  seed: null,
  loading: false,
  error: null,
};

/**
 * RFC 4122 UUID v1-5 format (8-4-4-4-12 hex с canonical version + variant
 * digits). URL query string — untrusted user input; гарантируем что мы не
 * forward'им мусор в IDB lookup или сохраняем не-UUID в `fromQuoteId`
 * (Zod ContractDraft.fromQuoteId.uuid() rejects).
 */
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

export function useFromQuoteSeeding(): FromQuoteSeedingResult {
  const [searchParams] = useSearchParams();
  const quoteId = searchParams.get('fromQuote');

  // Initial state — если quoteId есть, стартуем в loading. Avoids flash of
  // {seed: null, loading: false} перед первым effect run.
  const [state, setState] = useState<FromQuoteSeedingResult>(() =>
    quoteId === null ? NO_SEED : { seed: null, loading: true, error: null },
  );

  useEffect(() => {
    if (quoteId === null) {
      setState(NO_SEED);
      return;
    }

    // Reviewer Phase 3 medium: validate UUID format перед IDB lookup. URL —
    // untrusted; invalid → quote_not_found без сетевого/IDB hit.
    if (!UUID_RE.test(quoteId)) {
      setState({ seed: null, loading: false, error: 'quote_not_found' });
      return;
    }

    let cancelled = false;
    setState({ seed: null, loading: true, error: null });

    loadSavedQuote(quoteId).then(
      (quote) => {
        if (cancelled) return;
        if (!quote) {
          setState({ seed: null, loading: false, error: 'quote_not_found' });
          return;
        }
        setState({
          // PR-C2 Phase 2 (ADR-0036) — дополняем сид wall-clock-таймстампом
          // первого сева. mapping детерминирован (pure); seededFromQuoteAt
          // добавляется здесь, в impure-слое хука. lastQuoteUpdatedAt +
          // seededSpecHash mapping уже проставил.
          seed: { ...mapQuoteStateToContractDraft(quote), seededFromQuoteAt: Date.now() },
          loading: false,
          error: null,
        });
      },
      (err: unknown) => {
        if (cancelled) return;
        // Reviewer Phase 3 medium: НЕ log raw err — IDB DOMException может
        // содержать record data в message (P-009). Только err.name (e.g.
        // 'QuotaExceededError', 'InvalidStateError').
        if (import.meta.env.DEV) {
          const errName = err instanceof Error ? err.name : 'unknown';
          console.error('[useFromQuoteSeeding] loadSavedQuote failed:', errName);
        }
        setState({ seed: null, loading: false, error: 'idb_unavailable' });
      },
    );

    return () => {
      cancelled = true;
    };
  }, [quoteId]);

  return state;
}
